/**
 * Reusable View More/Less Button Functionality
 * 
 * This module provides generic functionality for view more/less buttons
 * that can be used across different blocks and components.
 */

class ViewMoreButton {
    constructor(container) {
        this.container = container;
        this.button = container.querySelector('.view-more-btn');
        this.isInitialized = false;
        this.isAnimating = false;
        this.boundHandleClick = null;
        
        if (!this.button) {
            console.warn('View more button not found in container');
            return;
        }
        
        this.config = this.parseConfig();
        
        // Validate configuration
        if (!this.validateConfig()) {
            console.warn('Invalid configuration for view more button');
            return;
        }
        
        this.visibleCount = parseInt(this.button.dataset.visibleCount) ?? this.config.initialVisible;
        this.targetContainer = this.findTargetContainer();
        
        if (!this.targetContainer) {
            console.warn('Target container not found for view more button');
            return;
        }
        
        this.init();
    }
    
    /**
     * Parse configuration from data attributes
     * @returns {Object} Configuration object
     */
    parseConfig() {
        const dataset = this.container.dataset;
        
        return {
            totalItems: parseInt(dataset.totalItems) ?? 0,
            initialVisible: parseInt(dataset.initialVisible) ?? 5,
            itemsPerClick: parseInt(dataset.itemsPerClick) ?? 5,
            containerClass: dataset.containerClass ?? '',
            itemClass: dataset.itemClass ?? '',
            indexAttribute: dataset.indexAttribute ?? 'data-index',
            viewMoreText: dataset.viewMoreText ?? 'VIEW MORE',
            viewLessText: dataset.viewLessText ?? 'VIEW LESS',
            showAnimationName: dataset.showAnimationName ?? 'viewMoreShow',
            hideAnimationName: dataset.hideAnimationName ?? 'viewMoreHide'
        };
    }
    
    /**
     * Validate configuration object
     * @returns {boolean} True if valid, false otherwise
     */
    validateConfig() {
        const { totalItems, initialVisible, itemsPerClick, containerClass, itemClass } = this.config;
        
        // Check required fields
        if (!containerClass || !itemClass) {
            console.warn('containerClass and itemClass are required');
            return false;
        }
        
        // Check numeric values
        if (totalItems < 0 || initialVisible < 0 || itemsPerClick < 1) {
            console.warn('Invalid numeric values in configuration');
            return false;
        }
        
        // Check if we need the button at all
        if (totalItems <= initialVisible) {
            return false;
        }
        
        return true;
    }
    
    /**
     * Initialize the button functionality
     */
    init() {
        if (this.isInitialized) return;
        
        this.addAccessibilityAttributes();
        this.updateButtonState();
        this.attachEventListeners();
        this.isInitialized = true;
        
        // Store reference on container for easy access
        this.container._viewMoreInstance = this;
    }
    
    /**
     * Add accessibility attributes
     */
    addAccessibilityAttributes() {
        this.button.setAttribute('aria-expanded', 'false');
        this.button.setAttribute('aria-controls', this.config.containerClass.replace('.', ''));
        
        if (this.targetContainer) {
            const containerId = this.targetContainer.id || `view-more-${Date.now()}`;
            this.targetContainer.id = containerId;
            this.button.setAttribute('aria-controls', containerId);
        }
    }
    
    /**
     * Attach event listeners
     */
    attachEventListeners() {
        this.boundHandleClick = this.handleButtonClick.bind(this);
        this.button.addEventListener('click', this.boundHandleClick);
    }
    
    /**
     * Handle button click events
     * @param {Event} event Click event
     */
    handleButtonClick(event) {
        event.preventDefault();
        
        // Prevent multiple clicks during animation
        if (this.isAnimating) {
            return;
        }
        
        this.isAnimating = true;
        const currentState = this.button.dataset.state || 'more';
        
        if (currentState === 'more') {
            this.showMoreItems();
        } else {
            this.showLessItems();
        }
        
        this.updateButtonState();
        
        // Reset animation lock after a delay
        setTimeout(() => {
            this.isAnimating = false;
        }, 300); // Adjust based on your CSS animation duration
    }
    
    /**
     * Show more items
     */
    showMoreItems() {
        const newVisible = Math.min(
            this.visibleCount + this.config.itemsPerClick, 
            this.config.totalItems
        );
        
        this.animateShowItems(this.visibleCount, newVisible);
        this.visibleCount = newVisible;
        this.button.dataset.visibleCount = this.visibleCount;
    }
    
    /**
     * Show less items (collapse back to initial)
     */
    showLessItems() {
        this.hideItems(this.config.initialVisible);
        this.visibleCount = this.config.initialVisible;
        this.button.dataset.visibleCount = this.visibleCount;
    }
    
    /**
     * Find the target container that holds the items
     * @returns {HTMLElement|null} The target container element
     */
    findTargetContainer() {
        if (!this.config.containerClass) return null;
        
        // Look for the container in the same parent or previous siblings
        const parent = this.container.closest('.betting-table__section, .block-container, .component');
        if (parent) {
            const found = parent.querySelector('.' + this.config.containerClass);
            if (found) return found;
        }
        
        // Fallback: look in the document
        return document.querySelector('.' + this.config.containerClass);
    }
    
    /**
     * Update button text and state
     */
    updateButtonState() {
        const isExpanded = this.visibleCount >= this.config.totalItems;
        
        if (isExpanded) {
            this.button.textContent = this.config.viewLessText;
            this.button.dataset.state = 'less';
            this.button.setAttribute('aria-expanded', 'true');
        } else {
            this.button.textContent = this.config.viewMoreText;
            this.button.dataset.state = 'more';
            this.button.setAttribute('aria-expanded', 'false');
        }
    }
    
    /**
     * Hide items beyond the specified count with animation
     * @param {number} count Number of items to keep visible
     */
    hideItems(count) {
        // Animate hiding the items that should be hidden
        for (let i = count; i < this.config.totalItems; i++) {
            const item = this.getItemByIndex(i);
            if (item && item.classList.contains(this.config.itemClass + '--visible')) {
                this.hideItem(item);
            }
        }
        
        // Ensure visible items are properly shown
        for (let i = 0; i < count; i++) {
            const item = this.getItemByIndex(i);
            if (item) {
                this.showItem(item);
            }
        }
    }
    
    /**
     * Animate showing new items
     * @param {number} fromIndex Start index
     * @param {number} toIndex End index
     */
    animateShowItems(fromIndex, toIndex) {
        for (let i = fromIndex; i < toIndex; i++) {
            const item = this.getItemByIndex(i);
            if (item) {
                this.showItemWithAnimation(item);
            }
        }
    }
    
    /**
     * Get all items by index (both desktop and mobile)
     * @param {number} index Item index
     * @returns {HTMLElement[]} Array of item elements
     */
    getAllItemsByIndex(index) {
        return Array.from(this.targetContainer.querySelectorAll(`[${this.config.indexAttribute}="${index}"]`));
    }
    
    /**
     * Get item by index
     * @param {number} index Item index
     * @returns {HTMLElement|null} The item element
     */
    getItemByIndex(index) {
        // Find all items with this index (should only be one now, but keep logic for compatibility)
        const allItems = this.getAllItemsByIndex(index);
        
        if (allItems.length === 0) {
            return null;
        }
        
        // If only one item, return it
        if (allItems.length === 1) {
            return allItems[0];
        }
        
        // If multiple items (shouldn't happen now, but keep for compatibility), return the visible one
        // Check computed style and class to determine which is visible
        let visibleItem = null;
        for (const item of allItems) {
            const style = window.getComputedStyle(item);
            const hasHiddenClass = item.classList.contains(this.config.itemClass + '--hidden');
            
            // Item is visible if:
            // 1. display is not 'none' (not hidden by CSS media query)
            // 2. visibility is not 'hidden' (not hidden by CSS)
            // 3. doesn't have the --hidden class (not hidden by JavaScript)
            if (style.display !== 'none' && 
                style.visibility !== 'hidden' && 
                !hasHiddenClass) {
                visibleItem = item;
                break; // Found the visible one, use it
            }
        }
        
        // If we found a visible item, return it
        if (visibleItem) {
            return visibleItem;
        }
        
        // Fallback: if no visible item found, return the one that's not hidden by CSS
        for (const item of allItems) {
            const style = window.getComputedStyle(item);
            if (style.display !== 'none' && style.visibility !== 'hidden') {
                return item;
            }
        }
        
        // Last resort: return the first one
        return allItems[0];
    }
    
    /**
     * Hide an item with animation
     * @param {HTMLElement} item The item to hide
     */
    hideItem(item) {
        item.classList.add(this.config.itemClass + '--hiding');
        
        const handleAnimationEnd = (event) => {
            // Only handle our specific animation
            if (event.animationName !== this.config.hideAnimationName) {
                return;
            }
            
            item.classList.remove(
                this.config.itemClass + '--visible', 
                this.config.itemClass + '--hiding'
            );
            item.classList.add(this.config.itemClass + '--hidden');
            item.removeEventListener('animationend', handleAnimationEnd);
        };
        
        item.addEventListener('animationend', handleAnimationEnd);
    }
    
    /**
     * Show an item without animation
     * @param {HTMLElement} item The item to show
     */
    showItem(item) {
        item.classList.remove(this.config.itemClass + '--hidden');
        item.classList.add(this.config.itemClass + '--visible');
    }
    
    /**
     * Show an item with animation
     * @param {HTMLElement} item The item to show
     */
    showItemWithAnimation(item) {
        item.classList.remove(this.config.itemClass + '--hidden');
        item.classList.add(
            this.config.itemClass + '--visible', 
            this.config.itemClass + '--showing'
        );
        
        const handleAnimationEnd = (event) => {
            // Only handle our specific animation
            if (event.animationName !== this.config.showAnimationName) {
                return;
            }
            
            item.classList.remove(this.config.itemClass + '--showing');
            item.removeEventListener('animationend', handleAnimationEnd);
        };
        
        item.addEventListener('animationend', handleAnimationEnd);
    }
    
    /**
     * Destroy the instance and clean up event listeners
     */
    destroy() {
        if (this.button && this.boundHandleClick) {
            this.button.removeEventListener('click', this.boundHandleClick);
        }
        
        // Clear references to prevent memory leaks
        this.container = null;
        this.button = null;
        this.targetContainer = null;
        this.boundHandleClick = null;
        this.isInitialized = false;
        this.isAnimating = false;
    }
    
    /**
     * Static method to initialize all view more buttons on the page
     */
    static initializeAll() {
        const containers = document.querySelectorAll('.view-more-container');
        const instances = [];
        
        containers.forEach(container => {
            // Skip if already initialized
            if (container.dataset.viewMoreInitialized === 'true') {
                return;
            }
            
            const instance = new ViewMoreButton(container);
            if (instance.button && instance.targetContainer && instance.isInitialized) {
                instances.push(instance);
                container.dataset.viewMoreInitialized = 'true';
            }
        });
        
        return instances;
    }
    
    /**
     * Static method to reinitialize buttons (useful for dynamic content)
     */
    static reinitialize() {
        // Clean up existing instances if needed
        return ViewMoreButton.initializeAll();
    }
    
    /**
     * Static method to get all initialized instances
     */
    static getInstances() {
        const containers = document.querySelectorAll('.view-more-container[data-view-more-initialized="true"]');
        return Array.from(containers).map(container => container._viewMoreInstance).filter(Boolean);
    }
    
    /**
     * Static method to destroy all instances
     */
    static destroyAll() {
        const instances = ViewMoreButton.getInstances();
        instances.forEach(instance => instance.destroy());
    }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    ViewMoreButton.initializeAll();
});

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = ViewMoreButton;
} 